import { forwardRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Container } from "./CommonStyles";
import { Checkbox, Typography } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
// noinspection ES6PreferShortImport
import { FormLabel } from "../form/form-label/FormLabel";
import { Box } from "@mui/material";

export interface CheckboxFieldProps {
    id: string;
    label: string;
    description?: string;
    value?: boolean;
    required?: boolean;
    disabled?: boolean;
    fontWeightBold?: boolean;
    className?: string;
    /*
     * props used when used to control collapsed sections
     */
    ariaExpanded?: boolean;
    ariaControls?: string;
}

const InlineContainer = styled(Container)(({ theme }) => ({
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        display: "flex", // override original
    },
    flexWrap: "wrap",
}));

const CheckboxContainer = styled(Box)(({ theme }) => ({
    flexBasis: "100%",
    flexShrink: 0,
    display: "flex",
    flexDirection: "row",
    columnGap: theme.spacing(1),
}));

/**
 * checkbox field
 */
export const CheckboxField = forwardRef<any, CheckboxFieldProps>(
    (
        {
            id,
            label,
            value,
            description,
            required = false,
            disabled = false,
            fontWeightBold = true,
            className,
            ariaExpanded,
            ariaControls,
        },
        ref
    ) => {
        const { control } = useFormContext();
        const descriptionElementId = `a11y-checkbox-${id}-description`;

        return (
            <InlineContainer className={className}>
                <CheckboxContainer>
                    <Controller
                        control={control}
                        name={id}
                        defaultValue={value}
                        rules={{ required: required }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <Checkbox
                                id={id}
                                slotProps={{ input: { ref } }}
                                onChange={onChange}
                                onBlur={onBlur}
                                checked={value}
                                disabled={disabled}
                                inputProps={{
                                    "aria-controls": ariaControls,
                                    "aria-expanded": ariaExpanded,
                                    "aria-describedby": description ? descriptionElementId : undefined,
                                }}
                            />
                        )}
                    />
                    <FormLabel htmlFor={id} text={label} required={required} fontWeightBold={fontWeightBold} />
                </CheckboxContainer>
                {description ? (
                    <Box>
                        <Typography sx={{ display: "block" }} variant="subtitle2" id={descriptionElementId}>
                            {description}
                        </Typography>
                    </Box>
                ) : null}
            </InlineContainer>
        );
    }
);
